/** Authoritative instructor tier order — matches backend TIER_LADDER. */
export const INSTRUCTOR_TIER_LADDER = [
  'starter',
  'professional',
  'growth',
  'organization',
  'enterprise',
];

/**
 * Lowest tier that includes each gated feature — mirrors backend FEATURE_MIN_TIER
 * (InstructorEntitlementService). Tiers are a ladder, so anything at or above the minimum
 * keeps the capability even when the plan matrix has no row for that tier.
 */
export const FEATURE_MIN_TIER = {
  ai_course_builder: 'growth',
  scorm_training: 'growth',
  invite_team_members: 'organization',
  api_access: 'enterprise',
  custom_ai_tutor_persona: 'enterprise',
};

/** Normalizes tier slugs for comparisons. */
export function normalizeTierSlug(tier) {
  if (!tier) {
    return '';
  }
  return String(tier).trim().toLowerCase();
}

/** Ladder index for a tier slug (-1 when unknown). */
export function tierRank(tier) {
  const slug = normalizeTierSlug(tier);
  const index = INSTRUCTOR_TIER_LADDER.indexOf(slug);
  return index >= 0 ? index : -1;
}

/** True when the tier sits at or above a gated feature's ladder minimum. */
export function tierIncludesFeature(tier, featureKey) {
  const minTier = FEATURE_MIN_TIER[featureKey];
  if (!minTier) {
    return false;
  }
  const rank = tierRank(tier);
  return rank >= 0 && rank >= tierRank(minTier);
}

/** Next tier slug above the current one, if any. */
export function nextTierSlug(currentTier) {
  const rank = tierRank(currentTier);
  if (rank < 0 || rank >= INSTRUCTOR_TIER_LADDER.length - 1) {
    return null;
  }
  return INSTRUCTOR_TIER_LADDER[rank + 1];
}

/** Sorts pricing rows by tier ladder order. */
export function sortPlansByTier(packages) {
  return [...packages].sort((a, b) => tierRank(a.tier) - tierRank(b.tier));
}

/**
 * Plans strictly above the viewer's current tier (entitlements.tier).
 * Falls back to all known tiers when current tier is unknown.
 */
export function filterUpgradeablePlans(packages, currentTier) {
  const list = Array.isArray(packages) ? packages : [];
  const currentRank = tierRank(currentTier);
  if (currentRank < 0) {
    return sortPlansByTier(list.filter((pkg) => tierRank(pkg.tier) >= 0));
  }
  return sortPlansByTier(
    list.filter((pkg) => tierRank(pkg.tier) > currentRank),
  );
}

/** Stable 1-based ids for legacy checkout helpers — ordered by tier rank. */
export function assignLegacyPlanIds(packages) {
  return sortPlansByTier(packages).map((pkg, index) => ({
    ...pkg,
    planId: index + 1,
  }));
}

/** Highlights the next tier up, otherwise the cheapest upgrade option. */
export function getRecommendedUpgradePlanId(currentTier, upgradeablePackages) {
  if (!upgradeablePackages.length) {
    return null;
  }
  const nextSlug = nextTierSlug(currentTier);
  if (nextSlug) {
    const nextPlan = upgradeablePackages.find(
      (pkg) => normalizeTierSlug(pkg.tier) === nextSlug,
    );
    if (nextPlan) {
      return nextPlan.planId;
    }
  }
  return upgradeablePackages[0].planId;
}

/** True when the viewer is on the top tier in the ladder. */
export function isHighestTier(currentTier) {
  const rank = tierRank(currentTier);
  return rank >= 0 && rank >= INSTRUCTOR_TIER_LADDER.length - 1;
}

/** True when target tier sits below the viewer's current tier on the ladder. */
export function isLowerTier(currentTier, targetTier) {
  const currentRank = tierRank(currentTier);
  const targetRank = tierRank(targetTier);
  if (currentRank < 0 || targetRank < 0) {
    return false;
  }
  return targetRank < currentRank;
}

/** True when the viewer may upgrade into the target tier (strictly above current). */
export function isUpgradeableTier(currentTier, targetTier) {
  const currentRank = tierRank(currentTier);
  const targetRank = tierRank(targetTier);
  if (targetRank < 0) {
    return false;
  }
  if (currentRank < 0) {
    return true;
  }
  return targetRank > currentRank;
}
